const MENU_QR_WALLET_PROVIDER_IDS = Object.freeze([
  "mercado_pago",
  "naranja_x",
  "uala",
  "modo",
]);

export const MENU_QR_WALLET_LAUNCH_MODES = Object.freeze({
  INTENT_URL: "intent_url",
  URL_TEMPLATE: "url_template",
});

export const MENU_QR_WALLET_PROVIDER_CATALOG = Object.freeze([
  {
    id: "mercado_pago",
    label: "Mercado Pago",
    accentColor: "#009ee3",
    description:
      "Usa el checkout base que hoy genera MozzoApp para cobrar desde el Menu QR.",
    defaultEnabled: true,
    defaultLaunchMode: MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL,
    defaultLaunchTemplate: "",
  },
  {
    id: "naranja_x",
    label: "Naranja X",
    accentColor: "#ff7a00",
    description:
      "Queda lista para cuando carguemos el deeplink final de Naranja X desde Backoffice.",
    defaultEnabled: false,
    defaultLaunchMode: MENU_QR_WALLET_LAUNCH_MODES.URL_TEMPLATE,
    defaultLaunchTemplate: "",
  },
  {
    id: "uala",
    label: "Uala",
    accentColor: "#7c3aed",
    description:
      "Usa el checkout real de Uala que MozzoApp genera al momento de cobrar desde el Menu QR.",
    defaultEnabled: false,
    defaultLaunchMode: MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL,
    defaultLaunchTemplate: "",
  },
  {
    id: "modo",
    label: "MODO",
    accentColor: "#2563eb",
    description:
      "Queda reservada para el flujo interoperable que abriremos desde MozzoApp.",
    defaultEnabled: false,
    defaultLaunchMode: MENU_QR_WALLET_LAUNCH_MODES.URL_TEMPLATE,
    defaultLaunchTemplate: "",
  },
]);

const MENU_QR_WALLET_PROVIDER_CATALOG_MAP = new Map(
  MENU_QR_WALLET_PROVIDER_CATALOG.map((provider) => [provider.id, provider])
);
const MENU_QR_WALLET_CHECKOUT_ENGINES = new Set([
  "mercado_pago",
  "modo",
  "uala",
  "naranja_x",
]);
const DIRECT_CHECKOUT_LINK_WALLETS = new Set(["mercado_pago", "uala"]);

const toSafeText = (value, maxLength = 1600) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toSafeBoolean = (value) => value === true;

export const normalizeMenuQrWalletCheckoutEngine = (value) => {
  const normalized = toSafeText(value, 80).toLowerCase();
  return MENU_QR_WALLET_CHECKOUT_ENGINES.has(normalized) ? normalized : "";
};

export const normalizeMenuQrWalletProviderId = (value) => {
  const normalized = toSafeText(value, 80).toLowerCase();
  return MENU_QR_WALLET_PROVIDER_IDS.includes(normalized)
    ? normalized
    : MENU_QR_WALLET_PROVIDER_CATALOG[0].id;
};

export const normalizeMenuQrWalletLaunchMode = (value) => {
  const normalized = toSafeText(value, 80).toLowerCase();
  return normalized === MENU_QR_WALLET_LAUNCH_MODES.URL_TEMPLATE
    ? MENU_QR_WALLET_LAUNCH_MODES.URL_TEMPLATE
    : MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL;
};

export const buildDefaultMenuQrWalletProviders = () =>
  MENU_QR_WALLET_PROVIDER_CATALOG.map((provider) => ({
    id: provider.id,
    enabled: provider.defaultEnabled,
    launchMode: provider.defaultLaunchMode,
    launchTemplate: provider.defaultLaunchTemplate,
    label: provider.label,
    accentColor: provider.accentColor,
    description: provider.description,
  }));

export const normalizeMenuQrWalletProvider = (value = {}) => {
  const providerId = normalizeMenuQrWalletProviderId(value?.id);
  const catalogEntry = MENU_QR_WALLET_PROVIDER_CATALOG_MAP.get(providerId);
  const isBuiltInIntentProvider =
    providerId === "mercado_pago" || providerId === "uala";
  const launchMode = isBuiltInIntentProvider
    ? MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL
    : normalizeMenuQrWalletLaunchMode(
        value?.launchMode || value?.launch_mode || catalogEntry?.defaultLaunchMode
      );
  const launchTemplate = isBuiltInIntentProvider
    ? ""
    : toSafeText(
        value?.launchTemplate ||
          value?.launch_template ||
          catalogEntry?.defaultLaunchTemplate,
        1600
      );

  return {
    id: providerId,
    label: catalogEntry?.label || providerId,
    accentColor: catalogEntry?.accentColor || "#0f172a",
    description: catalogEntry?.description || "",
    enabled:
      value?.enabled === undefined
        ? toSafeBoolean(catalogEntry?.defaultEnabled)
        : toSafeBoolean(value?.enabled),
    launchMode,
    launchTemplate,
  };
};

export const normalizeMenuQrWalletProviders = (value) => {
  const source = Array.isArray(value) ? value : [];
  const sourceMap = new Map(
    source
      .map((entry) => normalizeMenuQrWalletProvider(entry))
      .map((entry) => [entry.id, entry])
  );

  return MENU_QR_WALLET_PROVIDER_CATALOG.map((catalogEntry) =>
    normalizeMenuQrWalletProvider(
      sourceMap.get(catalogEntry.id) || {
        id: catalogEntry.id,
        enabled: catalogEntry.defaultEnabled,
        launchMode: catalogEntry.defaultLaunchMode,
        launchTemplate: catalogEntry.defaultLaunchTemplate,
      }
    )
  );
};

export const isMenuQrWalletProviderOperational = (provider) => {
  const normalizedProvider = normalizeMenuQrWalletProvider(provider);

  if (!normalizedProvider.enabled) {
    return false;
  }

  return (
    normalizedProvider.launchMode === MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL ||
    Boolean(normalizedProvider.launchTemplate)
  );
};

export const getOperationalMenuQrWalletProviders = (value) =>
  normalizeMenuQrWalletProviders(value).filter(isMenuQrWalletProviderOperational);

export const isMenuQrWalletProviderCompatibleWithPaymentProvider = (
  provider,
  paymentProvider = ""
) => {
  const normalizedProvider = normalizeMenuQrWalletProvider(provider);
  if (!isMenuQrWalletProviderOperational(normalizedProvider)) {
    return false;
  }

  if (normalizedProvider.id === "uala") {
    return true;
  }

  if (
    normalizedProvider.launchMode === MENU_QR_WALLET_LAUNCH_MODES.URL_TEMPLATE
  ) {
    return Boolean(normalizedProvider.launchTemplate);
  }

  const normalizedPaymentProvider =
    normalizeMenuQrWalletCheckoutEngine(paymentProvider);

  if (!normalizedPaymentProvider) {
    return false;
  }

  return normalizedProvider.id === normalizedPaymentProvider;
};

export const getCompatibleMenuQrWalletProviders = (
  value,
  paymentProvider = ""
) =>
  getOperationalMenuQrWalletProviders(value).filter((provider) =>
    isMenuQrWalletProviderCompatibleWithPaymentProvider(provider, paymentProvider)
  );

export const findMenuQrWalletProvider = (value, providerId) =>
  normalizeMenuQrWalletProviders(value).find(
    (provider) => provider.id === normalizeMenuQrWalletProviderId(providerId)
  ) || null;

const applyTemplateToken = (template, token, rawValue) => {
  const safeRawValue = rawValue === undefined || rawValue === null ? "" : `${rawValue}`;

  return template
    .split(`{${token}}`)
    .join(safeRawValue)
    .split(`{${token}_encoded}`)
    .join(encodeURIComponent(safeRawValue));
};

const isMercadoPagoCheckoutUrl = (value) => {
  try {
    const parsedUrl = new URL(toSafeText(value, 1600));
    const protocol = toSafeText(parsedUrl.protocol, 16).toLowerCase();
    const hostname = toSafeText(parsedUrl.hostname, 240).toLowerCase();

    return (
      (protocol === "https:" || protocol === "http:") &&
      hostname.includes("mercadopago.")
    );
  } catch (error) {
    return false;
  }
};

const isUalaCheckoutUrl = (value) => {
  try {
    const parsedUrl = new URL(toSafeText(value, 1600));
    const protocol = toSafeText(parsedUrl.protocol, 16).toLowerCase();
    const hostname = toSafeText(parsedUrl.hostname, 240).toLowerCase();

    return (
      (protocol === "https:" || protocol === "http:") &&
      (
        hostname.includes("uala-checkout.") ||
        hostname.includes("checkout.developers.ar.ua.la") ||
        hostname.includes("ualabis.com.ar")
      )
    );
  } catch (error) {
    return false;
  }
};

export const resolveMenuQrWalletLaunchUrl = ({
  paymentIntent,
  provider,
  branchName = "",
  customerName = "",
} = {}) => {
  const normalizedProvider = normalizeMenuQrWalletProvider(provider);
  const intentProvider = normalizeMenuQrWalletCheckoutEngine(paymentIntent?.provider);
  const intentUrl = toSafeText(
    paymentIntent?.deeplinkUrl || paymentIntent?.qr?.deeplinkUrl,
    1600
  );

  if (!intentUrl) {
    return "";
  }

  if (
    intentProvider &&
    !isMenuQrWalletProviderCompatibleWithPaymentProvider(
      normalizedProvider,
      intentProvider
    )
  ) {
    return "";
  }

  if (
    (intentProvider || normalizedProvider.id) === "mercado_pago" &&
    !isMercadoPagoCheckoutUrl(intentUrl)
  ) {
    return "";
  }

  if (intentProvider === "uala" && !isUalaCheckoutUrl(intentUrl)) {
    return "";
  }

  if (
    intentProvider &&
    intentProvider === normalizedProvider.id &&
    DIRECT_CHECKOUT_LINK_WALLETS.has(intentProvider)
  ) {
    return intentUrl;
  }

  if (normalizedProvider.launchMode === MENU_QR_WALLET_LAUNCH_MODES.INTENT_URL) {
    return intentUrl;
  }

  let resolvedTemplate = toSafeText(normalizedProvider.launchTemplate, 1600);

  if (!resolvedTemplate) {
    return "";
  }

  const replacements = {
    intent_url: intentUrl,
    external_reference: toSafeText(paymentIntent?.externalReference, 240),
    provider_operation_id: toSafeText(paymentIntent?.providerOperationId, 240),
    amount: toSafeText(paymentIntent?.grossAmount, 80),
    amount_cents: `${Math.round(Number(paymentIntent?.grossAmount || 0) * 100)}`,
    branch_name: toSafeText(branchName, 160),
    customer_name: toSafeText(customerName, 160),
  };

  Object.entries(replacements).forEach(([token, rawValue]) => {
    resolvedTemplate = applyTemplateToken(resolvedTemplate, token, rawValue);
  });

  return resolvedTemplate;
};

export const MENU_QR_WALLET_TEMPLATE_HELP =
  "Tokens: {intent_url}, {intent_url_encoded}, {external_reference}, {external_reference_encoded}, {provider_operation_id}, {amount}, {amount_cents}, {branch_name}, {customer_name}.";
